import {
  HTTP_INTERCEPTORS,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, effect } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { StartupErrorService } from './../../layout/components/startup-error/startup-error.service';

import { DialogStatus } from 'src/app/core/services/dialog.service';

import { AppIntroService } from 'src/app/layout/components/app-intro/app-intro.service';
import {
  AccountService,
  CustomerDomainsService,
  OrganizationsService,
} from '../services/../api/services';
import { AuthTokenService } from '../services/authentication/auth-token.service';
import { PermissionService } from '../services/authentication/permission.service';
import { AppConfigService } from '../services/config.service';
import { DialogService } from '../services/dialog.service';
import { ShowCreditPaymentForPlanService } from '../services/show-credit-payment-for-plan.service';
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  //#region properties
  isRefreshTokenInProgress = false;
  tokenRefreshedSource = new Subject();
  tokenSubject = new Subject();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();
  //#endregion

  constructor(
    private dialogService: DialogService,
    private translate: TranslateService,
    private permissionService: PermissionService,
    private appIntroService: AppIntroService,
    private accountService: AccountService,
    private appConfigService: AppConfigService,
    private startupErrorService: StartupErrorService
  ) {
    effect(() => {
      this.isRefreshTokenInProgress =
        this.permissionService.isRefreshingToken();
    });
  }

  handleResponseError(error, request?: HttpRequest<any>, next?: HttpHandler) {
    let errMsg = '';
    let errTitle = 'common.error';
    const apiUrl = AppConfigService.config.apiUrl;
    const requestUrl = request.url.replace(apiUrl, '');
    if (!this.appConfigService.configDone) return throwError(() => error);
    //Initial request and check server state
    console.log(
      'CustomerDomainsService.CustomerDomainsGetCurrentClientDataPath',
      CustomerDomainsService.CustomerDomainsGetCurrentClientDataPath
    );

    if (
      requestUrl.includes(
        CustomerDomainsService.CustomerDomainsGetCurrentClientDataPath
      )
    ) {
      this.getServerStateApiUrlApi();
      return throwError(() => error);
    }

    // Invalid token error
    if (error.status === 401) {
      if (
        requestUrl ==
        OrganizationsService.OrganizationsGetOrganizationCreditForAdminPath
      ) {
        this.isShowCreditPaymentTypeForPlan();
        return throwError(() => error);
      }
      if (requestUrl == '/Account/RefreshToken') {
        AuthTokenService.clearAllToken();
        return throwError(() => error);
      }

      return this.handle401Error(request, next);
    }

    // Access denied error
    else if (error.status === 403) {
      const ignoredPaths = [
        '/Informations/GetInformationsForDashboard',
        '/OutBoxMessages/GetOutBoxMessageDailyStatistics',
        '/OutBoxMessages/GetOutBoxMessageCategorizedDailyStatistics',
        '/OutBoxMessages/GetScheduledMessageStatisticsByOrganizationId',
        '/Organizations/GetOrganizationCredit',
        '/Users/GetUserCredit',
        '/Organizations/GetOrganizationCreditForAdmin',
        '/Organizations/GetCurrentOrganizationExpireDateForAdmin',
        '/SmartSmsRequests/GetActivePeriodicSmartSmsRequests',
        '/PrivateNumbers/GetCurrentUserPrivateNumbers',
        '/BlessedTombs',
        '/Publishers',
        '/NationalComputerGamesFoundation',
        '/LegalDigitalCulturalInstitutions',
        '/CulturalCenters',
        '/Mosques',
        '/Cinema',
        '/QuranicInstitutes',
        '/Libraries',
      ];

      if (requestUrl == '/Organizations/GetOrganizationCreditForAdmin')
        this.isShowCreditPaymentTypeForPlan();

      if (!ignoredPaths.includes(requestUrl)) {
        errTitle = 'messages.htmlStatus.code403';
        errMsg = this.translate.instant('messages.htmlStatus.code403Msg');
        this.openErrorDialog(errTitle, errMsg);
      }
    }

    // NotFound error
    else if (error.status === 404) {
      const ignoredPaths = ['/Plans/GetPlan'];

      if (ignoredPaths.includes(requestUrl)) return throwError(() => error);

      const landingPageUrl = 'LandingPages/GetOrganizationLandingPage/';
      if (requestUrl.toLowerCase().includes(landingPageUrl.toLowerCase()))
        return throwError(() => error);

      errTitle = 'messages.htmlStatus.code404';
      errMsg = error?.error?.message;
      this.dialogService.openDialog({
        title: errTitle,
        message: errMsg,
        status: DialogStatus.error,
      });
    }

    // Method Not Allowed error
    else if (error.status === 405) {
      errTitle = 'messages.htmlStatus.code405';
      errMsg = this.translate.instant('messages.htmlStatus.code405Msg');
      this.openErrorDialog(errTitle, errMsg);
    }

    // Other Error
    else {
      const containsBulkRecipients = error?.error?.validationErrors?.some(
        (item) => item.propertyName.includes('BulkRecipients')
      );
      if (
        (requestUrl == '/ScheduledMessages/CreateScheduledMessageBulk2' ||
          requestUrl == '/ScheduledMessages/CreateScheduledMessageBulk') &&
        containsBulkRecipients
      ) {
        if (error.error.validationErrors) {
          let description = '';
          error.error.validationErrors.forEach((element) => {
            const index = parseInt(
              element?.propertyName?.split('[')[1].split(']')[0],
              10
            );
            errMsg = `● ${element.message}`;
            description +=
              request.body.bulkRecipients[index].description + '<br>';
          });
          this.openErrorDialog(errMsg, description);
        }
      } else {
        const ignoredPaths = [
          '/CustomerDomains/GetCurrentClientData',
          '/ScheduledMessages/CreateScheduledMessageBulk2',
        ];

        if (ignoredPaths.includes(requestUrl)) return throwError(() => error);

        //form validation Error
        if (error.error.validationErrors) {
          error.error.validationErrors.forEach((element) => {
            errMsg += `● ${element.message}<br>`;
          });
          this.openErrorDialog(errTitle, errMsg);
        } else if (error.error.message) {
          errTitle = 'common.error';
          errMsg = error.error.message;
          this.openErrorDialog(errTitle, errMsg);
        } else {
          errMsg = this.translate.instant('messages.htmlStatus.code500Msg');
          this.openErrorDialog(errTitle, errMsg);
        }
      }
    }

    return throwError(() => error);
  }

  getServerStateApiUrlApi() {
    const serverStateApiUrl = AppConfigService.config.serverStateApiUrl;
    const serverStateToken = AppConfigService.config.serverStateToken;
    fetch(serverStateApiUrl, {
      headers: { Authorization: serverStateToken },
    })
      .then((res) => res.text())
      .then((status) => {
        if (status == 'normal')
          this.startupErrorService.toggleShowServerError();
        else this.startupErrorService.toggleShowServerUpdate();
      })
      .catch((err) => {
        this.startupErrorService.toggleShowServerError();
      });
  }

  isShowCreditPaymentTypeForPlan() {
    ShowCreditPaymentForPlanService.isShowCreditType = false;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshTokenInProgress) {
      this.tokenSubject.next(null);
      this.isRefreshTokenInProgress = true;

      //get old tokens
      const accessToken = AuthTokenService.getAccessToken();
      const refreshToken = AuthTokenService.getRefreshToken();

      return this.accountService
        .accountRefreshToken({
          body: { accessToken: accessToken, refreshToken: refreshToken },
        })
        .pipe(
          switchMap((res) => {
            if (res.isSucceed) {
              this.tokenSubject.next(res);
              AuthTokenService.setAllToken(
                res.data.accessToken,
                res.data.refreshToken,
                AuthTokenService.isRememberMe()
              );
              return next.handle(
                this.addBearerAndHeaders(request, res.data.accessToken)
              );
            } else this.login(request.url);

            this.isRefreshTokenInProgress = false;
          }),
          catchError((error) => {
            this.isRefreshTokenInProgress = false;
            this.login(request.url);
            return throwError(() => error);
          }),
          finalize(() => {
            this.isRefreshTokenInProgress = false;
          })
        );
    } else {
      return this.tokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((res) => {
          return next.handle(
            this.addBearerAndHeaders(
              request,
              (res as any).data.accessToken // ServiceResultOfSecurityTokenDto TODO torbatian
            )
          );
        })
      );
    }
  }

  addBearerAndHeaders(req: HttpRequest<any>, token?: string): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: 'Bearer ' + `${token}` },
    });
  }

  openErrorDialog(
    title?: string,
    message?: string,
    status: DialogStatus = DialogStatus.error
  ) {
    this.dialogService.openDialog({
      title: title,
      message: message,
      status: status,
    });
  }

  login(reqUrl?) {
    if (reqUrl.includes('/Account/Logout')) {
      AuthTokenService.clearAllToken();
      //window.location.replace(window.location.origin);
    }

    if (!this.permissionService.isShowErr401Dialog) {
      this.permissionService.isShowErr401Dialog = true;
      AuthTokenService.clearAllToken();
      window.location.replace(window.location.origin);
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Handle response
    return next.handle(request).pipe(
      catchError((error) => {
        if (request.url.includes('/Account/RefreshToken')) {
          AuthTokenService.clearAllToken();
          window.location.replace(window.location.origin);
        }
        return this.handleResponseError(error, request, next);
      })
    );
  }
}

export const HttpErrorInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
];
